import React from 'react';
import ModelsAPI from '../../api/models';
import { CAROUSEL_IMAGE_SIDE, ImageSize } from '../../api/settings';
import { ModelConstraintsResponse } from '../../api/structs/model';
import { parseDimensions } from '../selectors/modelSettingsHelpers';

interface CarouselSizeFieldProps {
  /** The locked image model. */
  model: string;
  size: ImageSize;
  onChange: (size: ImageSize) => void;
  /** False only when the model lists sizes and not this one. */
  onMadeChange: (made: boolean) => void;
}

const HELP =
  "Pictures for pipelines that end in a carousel, made on the image model above. Video and its frames keep the image model's own size.";

const label = (size: ImageSize) => `${size.width} × ${size.height}`;
const valueOf = (size: ImageSize) => `${size.width}x${size.height}`;
export const sameSize = (a: ImageSize, b: ImageSize) => a.width === b.width && a.height === b.height;

/** The sizes a model's constraints list, in the order listed; none when it lists none. */
export const listedSizes = (constraints: ModelConstraintsResponse): ImageSize[] => {
  const sizes: ImageSize[] = [];
  for (const entry of constraints.fields?.dimensions?.enum ?? []) {
    const size = parseDimensions(entry);
    if (size && !sizes.some((known) => sameSize(known, size))) sizes.push(size);
  }
  return sizes;
};

/** Whole numbers only; anything else is a side still being typed. */
const typedSide = (text: string): number | undefined => {
  const side = Number(text);
  return text.trim() !== '' && Number.isInteger(side) ? side : undefined;
};

/**
 * The size of the pictures of pipelines that end in a carousel. It is picked
 * from the sizes the locked image model makes; a model that lists none (or a
 * registry that cannot be read) takes a typed size, which the server checks.
 * A size the model does not make is shown as such and reported through
 * `onMadeChange`, so the form will not save it. While the sizes load the size
 * is plain text and nothing is held back.
 */
const CarouselSizeField: React.FC<CarouselSizeFieldProps> = ({ model, size, onChange, onMadeChange }) => {
  const noteId = React.useId();
  // Tagged with its model, so an answer for the model before is never read as this one's.
  const [loaded, setLoaded] = React.useState<{ model: string; sizes: ImageSize[] } | null>(null);
  // The side being typed, as text, until the field is left.
  const [typed, setTyped] = React.useState<Partial<Record<keyof ImageSize, string>>>({});

  React.useEffect(() => {
    if (!model) return;
    let cancelled = false;
    const answer = (sizes: ImageSize[]) => {
      if (!cancelled) setLoaded({ model, sizes });
    };
    ModelsAPI.getModelConstraints(model, 'image')
      .then((constraints) => answer(listedSizes(constraints)))
      .catch(() => answer([]));
    return () => {
      cancelled = true;
    };
  }, [model]);

  const sizes = loaded?.model === model ? loaded.sizes : undefined;
  const listed = sizes !== undefined && sizes.length > 0;
  const made = !listed || sizes.some((known) => sameSize(known, size));

  // Before the paint: Save must never be live for a frame with a size the model does not make.
  React.useLayoutEffect(() => {
    onMadeChange(made);
  }, [made, onMadeChange]);

  const help = <p className="mt-1 text-[11px] text-[var(--app-text-muted)]">{HELP}</p>;

  if (sizes === undefined) {
    return (
      <div>
        <p className="text-sm text-[var(--app-text)]">{label(size)}</p>
        {help}
      </div>
    );
  }

  if (!listed) {
    const side = (key: keyof ImageSize, name: string) => (
      <label className="flex items-center gap-2 text-xs text-[var(--app-text-muted)]">
        {name}
        <input
          type="number"
          aria-label={`Carousel ${key}`}
          className="input w-28"
          min={CAROUSEL_IMAGE_SIDE.min}
          max={CAROUSEL_IMAGE_SIDE.max}
          step={1}
          value={typed[key] ?? String(size[key])}
          onChange={(e) => {
            const text = e.target.value;
            setTyped((current) => ({ ...current, [key]: text }));
            const value = typedSide(text);
            if (value !== undefined) onChange({ ...size, [key]: value });
          }}
          onBlur={() => setTyped((current) => ({ ...current, [key]: undefined }))}
        />
      </label>
    );
    return (
      <div>
        <div className="flex flex-wrap items-center gap-4">
          {side('width', 'Width')}
          {side('height', 'Height')}
        </div>
        {help}
      </div>
    );
  }

  return (
    <div>
      <select
        aria-label="Carousel image size"
        aria-invalid={!made}
        aria-describedby={made ? undefined : noteId}
        className="select w-full text-sm"
        value={valueOf(size)}
        onChange={(e) => {
          const picked = parseDimensions(e.target.value);
          if (picked) onChange(picked);
        }}
      >
        {!made && <option value={valueOf(size)}>{`${label(size)} (not made by this model)`}</option>}
        {sizes.map((known) => (
          <option key={valueOf(known)} value={valueOf(known)}>
            {label(known)}
          </option>
        ))}
      </select>
      {!made && (
        <p id={noteId} className="mt-1 text-xs text-amber-400">
          {`Your image model doesn't make ${label(size)}. Pick a size it makes.`}
        </p>
      )}
      {help}
    </div>
  );
};

export default CarouselSizeField;
